import { useProductStore } from '../../store/productStore'
import type { Product } from '../../types'
import { Apple, Leaf, Package } from 'lucide-react'

const statusConfig: Record<Product['status'], { label: string; dot: string; bg: string; text: string }> = {
  active:    { label: 'מכירה פעילה', dot: '#40916C', bg: '#D8F3DC', text: '#1B4332' },
  sale:      { label: 'מבצע',        dot: '#F0A500', bg: '#FFF3CD', text: '#856404' },
  sold_out:  { label: 'אזל',         dot: '#E74C3C', bg: '#FFE8E0', text: '#C0392B' },
  suspended: { label: 'מושהה',       dot: '#F0F6FC', bg: '#6e6660', text: '#F0F6FC' },
  draft:     { label: 'טיוטה',       dot: '#6366F1', bg: '#EEF2FF', text: '#3730A3' },
  other:     { label: 'אחר',         dot: '#A89F97', bg: '#F2EDE4', text: '#6B6560' },
}

const unitLabels: Record<Product['unit'], string> = {
  kg: 'ק"ג', unit: 'יחידה', carton: 'קרטון', pallet: 'משטח', container: 'מיכל',
}

const CategoryIcon = ({ category }: { category: string | undefined }) => {
  const cls = "w-5 h-5 text-[#6B6560]"
  if (category === 'fruits')     return <Apple className={cls} />
  if (category === 'vegetables') return <Leaf  className={cls} />
  return <Package className={cls} />
}

interface Props {
  product: Product
  onEdit: () => void
}

export default function AdminProductRow({ product, onEdit }: Props) {
  const { deleteProduct, updateProduct } = useProductStore()
  const status = statusConfig[product.status]

  return (
    <div className="bg-white rounded-[14px] px-4 py-3 shadow-[0_2px_8px_rgba(44,24,16,0.06)] transition-shadow duration-200 ease-out hover:shadow-[0_4px_12px_rgba(44,24,16,0.09)] overflow-hidden">

      {/* top row — always visible */}
      <div className="flex items-start lg:items-center gap-2 sm:gap-6 min-w-0">

        {/* item image/ emoji */}
        <div className="w-12 h-12 lg:w-10 lg:h-10 rounded-[10px] bg-cream flex items-center justify-center shrink-0 overflow-hidden">
          {product.images?.[0]
            ? <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            : <CategoryIcon category={product.category} />
          }
        </div>

        {/* name + price */}
        <div className="flex-1 space-y-1 lg:space-y-0 text-right min-w-0">
          <p className="font-semibold text-text-primary text-md lg:text-sm truncate">{product.name}</p>
          <p className="text-md lg:text-xs text-text-muted">₪{product.price} / {unitLabels[product.unit]}</p>
          {(product.custom_sku || product.sku) && (
            <p className="text-xs lg:text-[10px] text-[#6E6660]">
              {product.custom_sku
                ? `${product.custom_sku} / מק״ט ${product.sku}`
                : `מק״ט ${product.sku}`
              }
            </p>
          )}
        </div>

        {/* status badge — desktop only */}
        <span
          className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0"
          style={{ background: status.bg, color: status.text }}
        >
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: status.dot }} />
          {status.label}
        </span>

        {/* quantity — desktop only */}
        <span className="hidden sm:block text-xs text-text-secondary bg-cream-dark px-2.5 py-1 rounded-lg shrink-0 w-20 text-center">
          {product.quantity} יח׳
        </span>

        {/* status select — desktop only */}
        <select
          value={product.status}
          onChange={(e) => updateProduct(product.id, { status: e.target.value as Product['status'] })}
          className="hidden sm:block text-xs px-2 h-[34px] rounded-[8px] border border-cream-dark text-text-secondary bg-white outline-none focus:border-green-primary cursor-pointer shrink-0 appearance-none"
          // className="hidden sm:block text-xs px-2 py-1.5 rounded-[8px] border border-cream-dark text-text-secondary bg-white outline-none focus:border-green-primary cursor-pointer shrink-0"
        >
          {Object.entries(statusConfig).map(([value, cfg]) => (
            <option key={value} value={value}>{cfg.label}</option>
          ))}
        </select>

        {/* actions */}
        <div className="flex gap-2.5 lg:gap-1 shrink-0">
          <button
            onClick={onEdit}
            className="text-md lg:text-xs px-3 sm:px-3 py-1.5 rounded-[8px] border border-cream-dark text-text-secondary bg-white hover:bg-cream transition-colors cursor-pointer"
          >
            עריכה
          </button>
          <button
            onClick={() => {
              if (confirm(`אתה עומד למחוק את המוצר ${product.name}. האם אתה בטוח?`)) {
                deleteProduct(product.id)
              }
            }}
            className="text-md lg:text-xs px-3 sm:px-3 py-1.5 rounded-[8px] border border-[#FFE8E0] text-[#C0392B] bg-white hover:bg-[#FFE8E0] transition-colors cursor-pointer"
          >
            מחק
          </button>
        </div>
      </div>

      {/* mobile bottom row */}
      <div className="flex sm:hidden items-center justify-between mt-2 pt-2 border-t border-cream-dark gap-2">
        <span
          className="flex items-center gap-1.5 px-2.5 py-1.5 lg:py-1 rounded-full text-[11px] font-semibold"
          style={{ background: status.bg, color: status.text }}
        >
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: status.dot }} />
          {status.label}
        </span>

        <select
          id={product.status}
          name={product.status}
          value={product.status}
          onChange={(e) => updateProduct(product.id, { status: e.target.value as Product['status'] })}
          className="text-sm lg:text-xs px-2 h-[40px] lg:h-[34px] rounded-[8px] border border-cream-dark text-text-secondary bg-white outline-none focus:border-green-primary cursor-pointer flex-1 mx-2 appearance-none"
        >
          {Object.entries(statusConfig).map(([value, cfg]) => (
            <option key={value} value={value}>{cfg.label}</option>
          ))}
        </select>

        <span className="text-md lg:text-xs text-text-muted shrink-0">{product.quantity} יח׳</span>
      </div>
    </div>
  )
}